import React, { useState } from "react"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterSegment, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

// Режимы отчёта — не фильтры запроса, применяются сразу
const metricOptions = [
	{ id: "revenue", label: "Выручка" },
	{ id: "cheques", label: "Чеки" },
	{ id: "average_cheque", label: "Ср. чек" },
]

const groupOptions = [
	{ id: "hours", label: "По часам", icon: "uil-clock" },
	{ id: "dayparts", label: "По дейпартам", icon: "uil-sun" },
]

const daypartLabels = {
	breakfast: "Завтрак",
	lunch: "Обед",
	afternoon: "Полдник",
	dinner: "Ужин",
	night: "Ночь",
	"Завтрак": "Завтрак",
	"Обед": "Обед",
	"Полдник": "Полдник",
	"Перекус": "Полдник",
	"Ужин": "Ужин",
	"Ночь": "Ночь",
}

// Шкала нагрузки: от низкой (светло-зелёный) к высокой (красный).
const HEAT_COLORS = [
	{ background: "var(--dark-tint-success, #e4f8ec)", color: "var(--dark-fg-success, #2f9e63)" },
	{ background: "#4cc47c", color: "#ffffff" },
	{ background: "#e7b73c", color: "#ffffff" },
	{ background: "#ec7d3b", color: "#ffffff" },
	{ background: "#e0492f", color: "#ffffff" },
]

const PEAK_DECORATIONS = ["🔥", "⚡", ""]

// Значение в единицах метрики: чеки — с десятыми (среднее за день), деньги — целые
function formatMetricValue(metric, value) {
	const number = Number(value || 0)
	if (metric === "cheques") return formatMoney(number, 1)
	return formatMoney(number, 0)
}

// Компактная подпись в ячейке
function formatCell(metric, value) {
	const number = Number(value || 0)
	if (number <= 0) return ""
	if (metric === "cheques") return number >= 100 ? formatMoney(Math.round(number), 0) : number.toFixed(1).replace(/\.0$/, "")
	return formatMoney(Math.round(number), 0)
}

function formatDaypartLabel(column) {
	if (column?.display_label) return column.display_label
	const label = daypartLabels[column?.key] || daypartLabels[column?.label] || column?.label
	return column?.time_label ? `${label} (${column.time_label})` : label
}

function heatLevel(value, min, max) {
	const number = Number(value || 0)
	if (number <= 0) return 0
	if (max <= min) return 2
	const ratio = (number - min) / (max - min)
	return Math.min(4, Math.max(0, Math.floor(ratio * 5)))
}

function cellTitle(metric, group, row, column, cell) {
	const columnLabel = group === "dayparts" ? formatDaypartLabel(column) : (column?.label || "")
	return `${row.label} ${columnLabel}: ${formatMetricValue(metric, cell.value)}`
		+ ` · всего за период: ${formatMoney(Number(cell.cheques || 0), 0)} чеков, ${formatMoney(Number(cell.revenue || 0), 0)}`
		+ `; дней в периоде: ${Number(cell.days || 0)}`
}

function HeatGrid({ data }) {
	const columns = data?.columns || []
	const rows = data?.rows || []
	const metric = data?.metric || "revenue"
	const group = data?.group || "hours"
	const min = Number(data?.min || 0)
	const max = Number(data?.max || 0)

	if (columns.length === 0 || rows.length === 0) {
		return <div className="text-muted text-center py-5">Нет данных за выбранный период</div>
	}

	return (
		<div style={{ overflowX: "auto" }}>
			<table style={{ borderCollapse: "separate", borderSpacing: "4px", width: "100%" }}>
				<thead>
					<tr>
						<th style={{ width: "44px" }}></th>
						{columns.map(column => (
							<th
								key={column.key}
								className="text-center"
								style={{ ...mutedStyle, fontWeight: 600, padding: "4px 0" }}
							>
								{group === "dayparts" ? formatDaypartLabel(column) : column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map(row => (
						<tr key={row.key}>
							<td style={{ ...mutedStyle, fontWeight: 600, paddingRight: "8px", whiteSpace: "nowrap" }}>
								{row.label}
							</td>
							{row.cells.map((cell, index) => {
								const level = heatLevel(cell.value, min, max)
								const palette = HEAT_COLORS[level]
								return (
									<td key={`${row.key}-${columns[index]?.key || index}`} style={{ padding: 0 }}>
										<div
											className="d-flex align-items-center justify-content-center"
											title={cellTitle(metric, group, row, columns[index], cell)}
											style={{
												height: "34px",
												minWidth: "48px",
												borderRadius: "7px",
												background: palette.background,
												color: palette.color,
												fontSize: "11px",
												fontWeight: 600,
											}}
										>
											{formatCell(metric, cell.value)}
										</div>
									</td>
								)
							})}
						</tr>
					))}
				</tbody>
			</table>

			<div className="d-flex align-items-center gap-2 mt-3" style={mutedStyle}>
				<span>Низкая нагрузка</span>
				<div className="d-flex gap-1">
					{HEAT_COLORS.map((palette, index) => (
						<span
							key={index}
							style={{ width: "22px", height: "14px", borderRadius: "4px", background: palette.background }}
						></span>
					))}
				</div>
				<span>Высокая нагрузка</span>
			</div>
		</div>
	)
}

function TotalsSummary({ totals }) {
	if (!totals) return null
	const returns = Number(totals.returns || 0)
	return (
		<div className="mb-3" style={mutedStyle}>
			Выручка за период <b style={{ color: "var(--dark-text-strong, #111827)" }}>{formatMoney(Number(totals.revenue || 0), 0)}</b>
			{returns > 0 && ` (продажи ${formatMoney(Number(totals.sales || 0), 0)} − возвраты ${formatMoney(returns, 0)})`}
			{" · "}Чеков <b style={{ color: "var(--dark-text-strong, #111827)" }}>{formatMoney(Number(totals.cheques || 0), 0)}</b>
		</div>
	)
}

function HeatMap() {
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
		metric: "cheques",
		group: "hours",
	}, searchWithFilter)
	const { draft, applied, patchApplied } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/heat-map", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function exportExcel() {
		if (!data) return
		const columns = data.columns || []
		const rows = data.rows || []
		const header = ["", ...columns.map(column => column.label)]
		const tableRows = rows
			.map(row => `<tr><td>${row.label}</td>${row.cells.map(cell => `<td>${Number(cell.value || 0)}</td>`).join("")}</tr>`)
			.join("")
		const html = `<table border="1"><thead><tr>${header.map(item => `<th>${item}</th>`).join("")}</tr></thead><tbody>${tableRows}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: "application/vnd.ms-excel" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = `heat-map-${data.start_date}_${data.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	const peakHours = data?.peak_hours || []
	const metric = data?.metric || applied.metric

	return (
		<>
			<AnalyticsFilterBar
				title="Тепловая карта"
				icon="uil-fire"
				subtitle="Нагрузка по дням недели и часам"
				filters={filters}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel}>
						<i className="uil uil-file-export me-1"></i>
						Excel
					</button>
				}
			>
				<StandardFields filters={filters} options={options} />
			</AnalyticsFilterBar>

			<div className="card" style={cardStyle}>
				<div className="card-body">
					<div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
						<div>
							<h5 className="mb-1 d-flex align-items-center gap-2">
								<i className="uil uil-table"></i>
								{applied.group === "dayparts" ? "Тепловая карта по дейпартам и дням недели" : "Тепловая карта по часам и дням недели"}
							</h5>
							{data?.metric_hint && <div style={mutedStyle}>{data.metric_hint}</div>}
						</div>
						<div className="d-flex flex-wrap align-items-center gap-2">
							<FilterSegment options={metricOptions} value={applied.metric} onChange={(value) => patchApplied({ metric: value })} />
							<FilterSegment options={groupOptions} value={applied.group} onChange={(value) => patchApplied({ group: value })} />
						</div>
					</div>

					<TotalsSummary totals={data?.totals} />

					{peakHours.length > 0 && (
						<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
							<span style={mutedStyle}>Пиковые часы по {(data?.metric_label || "").toLowerCase()}:</span>
							{peakHours.map((peak, index) => (
								<span
									key={peak.hour}
									className="d-inline-flex align-items-center gap-1"
									style={{
										background: "rgba(var(--bs-primary-rgb), 0.1)",
										color: "var(--bs-primary)",
										borderRadius: "14px",
										padding: "2px 10px",
										fontSize: "12px",
										fontWeight: 600,
									}}
								>
									<i className="uil uil-arrow-growth"></i>
									{peak.label}
									<span style={{ opacity: 0.75 }}>· {formatMetricValue(metric, peak.value)}</span>
									{PEAK_DECORATIONS[index] ? ` ${PEAK_DECORATIONS[index]}` : ""}
								</span>
							))}
						</div>
					)}

					<HeatGrid data={data} />
				</div>
			</div>
		</>
	)
}

export default HeatMap
